import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../styles/Menu.css';

const menuVariants = {
  hidden: {
    x: '100%',
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
      duration: 0.5,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 40,
      duration: 0.4,
      when: "afterChildren",
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

const linkVariants = {
  hidden: {
    opacity: 0,
    x: 50,
    y: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    x: 30,
    y: -10,
    transition: {
      duration: 0.2,
    },
  },
};

const closeButtonVariants = {
  hidden: {
    opacity: 0,
    scale: 0.5,
    rotate: -180,
  },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      delay: 0.1,
      type: 'spring',
      stiffness: 400,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.5,
    rotate: 180,
    transition: {
      duration: 0.2,
    },
  },
};

const ctaVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    y: 30,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      delay: 0.5,
      type: 'spring',
      stiffness: 300,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    y: 30,
    transition: {
      duration: 0.2,
    },
  },
};

const menuItems = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Case Studies', path: '/case-studies' },
  { name: 'About', path: '/about-us' },
  { name: 'Contact', path: '/contact' }
];

const Menu = ({ toggle, isOpen }) => {
  // Prevent body scrolling when menu is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      toggle();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Focus management for accessibility
      const closeButton = document.querySelector('.qf-menu-close-button');
      if (closeButton) {
        closeButton.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <motion.nav
      className="qf-nav-mobile"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={menuVariants}
      aria-hidden={!isOpen}
      role="navigation"
      aria-label="Mobile navigation menu"
    >
      {/* Enhanced close button with better visibility */}
      <motion.button
        className="qf-menu-close-button"
        onClick={toggle}
        aria-label="Close navigation menu"
        variants={closeButtonVariants}
        whileHover={{ 
          scale: 1.1,
          backgroundColor: "rgba(0, 255, 255, 0.25)",
          boxShadow: "0 0 30px rgba(0, 255, 255, 0.5)"
        }}
        whileTap={{ scale: 0.9 }}
        tabIndex={0}
      >
        <motion.svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          animate={{ rotate: isOpen ? 0 : 180 }}
          transition={{ duration: 0.3 }}
          aria-hidden="true"
        >
          <motion.path 
            d="M18 6L6 18" 
            stroke="#00FFFF" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            filter="drop-shadow(0 0 4px rgba(0, 255, 255, 0.6))"
          />
          <motion.path 
            d="M6 6L18 18" 
            stroke="#00FFFF" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.3, delay: 0.15 }}
            filter="drop-shadow(0 0 4px rgba(0, 255, 255, 0.6))"
          />
        </motion.svg>
      </motion.button>

      {/* Menu content container with better spacing */}
      <div className="qf-menu-content">
        {/* Brand section */}
        <motion.div 
          className="qf-menu-brand"
          variants={linkVariants}
        >
          <h2>QuantumFrame</h2>
          <p>Digital Excellence</p>
        </motion.div>

        {/* Enhanced navigation links */}
        <motion.div className="qf-nav-links-container">
          {menuItems.map((item, index) => (
            <motion.div
              key={item.name}
              variants={linkVariants}
              custom={index}
              className="qf-nav-link-wrapper"
            >
              <Link
                to={item.path}
                className="qf-nav-link-mobile"
                onClick={toggle}
                aria-label={`Navigate to ${item.name}`}
              >
                <motion.span
                  className="qf-nav-link-text"
                  whileHover={{ scale: 1.05, x: 10 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.name}
                </motion.span>
                <motion.div 
                  className="qf-nav-link-arrow"
                  whileHover={{ x: 5 }}
                  aria-hidden="true"
                >
                  →
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Enhanced CTA section */}
        <motion.div
          className="qf-nav-mobile-cta"
          variants={ctaVariants}
        >
          <Link
            to="/contact"
            onClick={toggle}
            className="qf-cta-button"
            aria-label="Get a free quote - Contact us"
          >
            <motion.span
              whileHover={{ 
                scale: 1.05,
                textShadow: "0 0 8px rgba(0, 255, 255, 0.6)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              Get a Free Quote
            </motion.span>
          </Link>
          
          {/* Contact info */}
          <motion.div 
            className="qf-contact-info"
            variants={linkVariants}
          >
            <p>Ready to start your project?</p>
            <p>Let's discuss your vision</p>
          </motion.div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default Menu;